import { inject, Injectable, makeEnvironmentProviders } from '@angular/core';
import { SKY_SESSION_STORAGE, SKY_SESSION_STORAGE_TOKEN } from '@core/tokens';
import { SessionStorageType } from '@core/enums';

@Injectable({
  providedIn: 'root',
})
export class SessionStorageService {
  private readonly storage: Storage = inject(SKY_SESSION_STORAGE);

  /**
   * Add item to session storage
   * @param key A unique key of `SessionStorageType` type.
   * @param value Item value.
   *
   */
  setItem(key: SessionStorageType | string, value: unknown): void {
    const valueJSON = JSON.stringify(value);

    if (valueJSON == null) {
      throw Error(`The ${value} has occur with empty value, please use removeItem instead.`);
    }

    this.storage.setItem(key, valueJSON);
  }

  /**
   * Get item from session storage
   * @param key A unique key of `SessionStorageType` type.
   *
   */
  getItem(key: SessionStorageType | string): string | null {
    return this.storage.getItem(key);
  }

  /**
   * Get item from session storage parsed
   * @param key A unique key of `SessionStorageType` type.
   *
   */
  getItemParsed<T>(key: SessionStorageType | string): T | null {
    const item = this.getItem(key);
    return item != null ? JSON.parse(item) : null;
  }

  /**
   * Check if item is exist in session storage
   * @param key A unique key of `SessionStorageType` type.
   *
   */
  hasItem(key: SessionStorageType | string): boolean {
    return key in this.storage;
  }

  /**
   * Remove item from session storage
   * @param key A unique key of `SessionStorageType` type.
   *
   */
  removeItem(key: SessionStorageType | string): void {
    this.storage.removeItem(key);
  }
}

export const sessionStorageProvider = {
  provide: SKY_SESSION_STORAGE_TOKEN,
  useClass: SessionStorageService,
};

export function provideSessionStorage() {
  return makeEnvironmentProviders([SessionStorageService, sessionStorageProvider]);
}
